import React, { useEffect, useState } from 'react'
import { Accordion, Modal, MODE_CONSTANTS, SvgEdit, Tooltip } from '@ui-kit'
import { hideLoader, showLoader } from '@app-store'
import { isEmpty } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import {
  componentNames,
  MISSING_INFO_POPUP_CONSTANTS,
  NAMESPACES,
} from '../../utils/constants'
import { getEntityDataByIdEmbedded } from '../../apis/entityData.api'
import { RowRenderTabsTypes } from '../../types/types'
import AddEditRecordPage from '../../pages/AddEditViewRecord.page'
import {
  getElementFromAddedFields,
  auditFields,
  formatDataBeforeLoad,
} from '../../utils/form.utils'
import { ViewRenderer } from '../FormRenderer'
import ElementRenderer from '../ElementRenderer'
import { EntityLayout } from '../../types/layout.types'
import { hasSensitiveDataPermission } from '../../utils/helperFunctions'
import { isReadOnlyUser } from '../../utils/permission'
import { FormAncestryScope } from '../../form-behavior/FormAncestryScope'
import {
  getEffectiveHidden,
  isRowHiddenByFormBehavior,
} from '../../form-behavior/formBehavior.utils'
import { getFormBehaviorRuleKey } from '../../form-behavior/useFormBehaviorFieldOverrides'
import { useFormBehaviorState } from '../../form-behavior/useFormBehaviorState'
import { useLayoutRenderTabsContext } from './LayoutRenderTabsContext'

const RowRenderTabs = ({
  row,
  layout,
  data,
  editLayout,
  index,
  is360 = false,
  tabIndexMap,
  parentLayout,
  viewEntityRecordId,
  parentRow,
  isRootEntityTab = false,
}: RowRenderTabsTypes) => {
  const mode = MODE_CONSTANTS.VIEW

  const methods = useForm({ reValidateMode: 'onBlur' })
  const {
    setValue,
    register,
    control,
    reset,
    formState: { errors },
    clearErrors,
    getValues,
  } = methods

  const added_fields = layout?.layout_data?.added_fields || {}
  const dispatch = useAppDispatch()
  const [, forceUpdate] = useState(0)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isLetterConfig, setIsLetterConfig] = useState<boolean>(false)
  const printletterConfig = useAppSelector(
    (state: RootState) => state.workflowData.faxDetails
  ) // print letter configuration
  const formBehavior = useFormBehaviorState(layout?.uid)
  const columnCount = row?.columns?.length
  const maxRows = Math.max(
    ...(row?.columns?.map(
      (col) => col.elements.filter((el) => !el.is_hidden).length
    ) || [0])
  )
  const { setEdit } = useLayoutRenderTabsContext()
  useEffect(() => {
    if (data) {
      formatDataBeforeLoad(data, layout?.layout_data?.added_fields ?? {})
      reset(data)
    }
  }, [data, reset])

  const toggleEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen)
  }

  const handleSave = () => {
    setIsEditModalOpen(false)
    const stored = sessionStorage.getItem(
      MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA
    )
    if (stored) {
      let parsed = JSON.parse(stored)
      parsed = { ...parsed, openModal: true }
      sessionStorage.setItem(
        MISSING_INFO_POPUP_CONSTANTS.PENDING_MI_DATA,
        JSON.stringify(parsed)
      )
    }
    window.location.reload()
  }

  const handleLookupFieldDataFetch = () => {
    dispatch(showLoader())
    const result = Object.keys(added_fields).filter(
      (key) => added_fields[key]?.ui_data_type === componentNames?.SINGLE_LOOKUP
    )
    result?.forEach((element) => {
      const lookupAddedFields = added_fields?.[element]?.added_fields || {}
      if (!isEmpty(lookupAddedFields)) {
        const keyList = Object.keys(lookupAddedFields)
        if (
          added_fields?.[element]?.relationship?.target_entity &&
          data?.[element]?.id
        )
          getEntityDataByIdEmbedded(
            added_fields?.[element]?.relationship?.target_entity || '',
            Number(added_fields?.[element]?.relationship?.target_entity_id),
            keyList || [],
            data?.[element]?.id
          )
            .then((response) => {
              // setLookupStateObject((prev) => {
              //   return { ...prev, [element]: response?.data || {} }
              // })
              setValue(element, response?.data)
              dispatch(hideLoader())
            })
            .catch((error) => {
              console.error('Error fetching data:', error)
              dispatch(hideLoader())
            })
      }
    })
    forceUpdate((prev) => prev + 1)
    dispatch(hideLoader())
  }

  useEffect(() => {
    if (!isEmpty(layout) && !isEmpty(data)) handleLookupFieldDataFetch()
  }, [data, layout])

  useEffect(() => {
    if (
      printletterConfig &&
      row?.label === NAMESPACES.GENERAL_CASE_INFORMATION
    ) {
      setIsLetterConfig(true)
    }
  }, [printletterConfig, row])

  useEffect(() => {
    setEdit(() => {
      if (!isLetterConfig) toggleEditModal()
    })
  }, [])

  if (isRowHiddenByFormBehavior(row?.rowKey, formBehavior.hiddenOverrides)) {
    return null
  }

  const accordionContent = (
    <div className="w-full">
      <div>
        <Accordion
          id={row?.rowKey || row?.rowId}
          heading={
            <div className="flex justify-between items-center align-middle">
              <span>{row?.label}</span>
              {!isReadOnlyUser()
                ? editLayout &&
                  !parentRow?.lookup_meta?.hideEditButton &&
                  !isEmpty(data) && (
                    <div>
                      <SvgEdit
                        disabled={
                          isLetterConfig ||
                          (data.is_client_controlled &&
                            !hasSensitiveDataPermission())
                        }
                        onClick={(e) => {
                          if (isLetterConfig) return
                          e.stopPropagation()
                          toggleEditModal()
                        }}
                        onKeyDown={(e) => {
                          if (isLetterConfig) return
                          if (e?.key === 'Enter' || e?.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleEditModal()
                          }
                        }}
                        tabIndex={
                          isLetterConfig ? -1 : tabIndexMap?.tabIndexOffset
                        }
                      />
                    </div>
                  )
                : null}
            </div>
          }
          isDefaultActive={[0, 1, 2].includes(index ?? 0)}
          tabIndex={tabIndexMap?.tabIndexOffset}
        >
          <div
            className="p-5 grid w-full"
            style={{
              gridTemplateColumns: `repeat(${columnCount}, minmax(200px, 1fr))`,
              gridAutoRows: 'auto',
              alignContent: 'start',
              overflow: 'visible',
            }}
          >
            {row?.columns?.map((col, colIndex) => {
              return (
                <div
                  key={col.columnId}
                  className="grid gap-3 w-full"
                  style={{
                    gridTemplateRows: `repeat(${maxRows}, minmax(0, 1fr))`,
                    gridColumn: colIndex + 1,
                    alignItems: 'start',
                  }}
                >
                  {Array.from({ length: maxRows }, (_, rowIndex) => {
                    const el = col.elements[rowIndex]
                    if (!el) {
                      return (
                        <div
                          key={`empty-${colIndex}-${rowIndex}`}
                          className=""
                        ></div>
                      )
                    }
                    const element = getElementFromAddedFields(
                      el,
                      layout?.entity_id?.toString() ?? '',
                      layout?.layout_data?.added_fields
                    )
                    const ruleKey = getFormBehaviorRuleKey(element)
                    if (
                      getEffectiveHidden(
                        ruleKey,
                        el.is_hidden,
                        formBehavior.hiddenOverrides
                      )
                    ) {
                      return null
                    }
                    if (auditFields.includes(element.keyName)) {
                      return (
                        <div
                          key={`audit-${colIndex}-${rowIndex}`}
                          className=""
                        ></div>
                      )
                    }
                    return (
                      <div key={el.id} className="w-full break-words">
                        {mode === MODE_CONSTANTS.VIEW &&
                          element?.elementName !==
                            componentNames.FILE_UPLOAD && (
                            <ViewRenderer
                              element={element}
                              tabIndex={tabIndexMap?.tabIndexOffset}
                              layout={parentLayout as EntityLayout}
                            />
                          )}
                        <div
                          className={
                            mode === MODE_CONSTANTS.VIEW &&
                            element?.elementName !== componentNames.FILE_UPLOAD
                              ? 'hidden'
                              : ''
                          }
                        >
                          <Tooltip content={element.meta.tooltip_text ?? ''}>
                            <ElementRenderer
                              data={data}
                              element={{
                                ...element,
                                meta: {
                                  ...element.meta,
                                  default_value: '',
                                },
                                tabIndex: tabIndexMap?.tabIndexOffset,
                              }}
                              register={register}
                              errors={errors}
                              setValue={setValue}
                              control={control}
                              clearErrors={clearErrors}
                              mode={mode}
                              getValues={getValues}
                              fullLayout={layout as EntityLayout}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </Accordion>
      </div>
    </div>
  )

  return (
    <div>
      {isEditModalOpen && !is360 && (
        <Modal
          isOpen={isEditModalOpen}
          showCloseButton={false}
          closeModal={() => {
            setIsEditModalOpen(false)
          }}
          width="w-[80%]"
          isResizable
        >
          <div>
            {editLayout ? (
              <AddEditRecordPage
                mode="edit"
                entityId={editLayout?.entity_id?.toString()}
                id={viewEntityRecordId ?? data?.id}
                loaderVisible={false}
                allowNavigate={false}
                onSaveClick={handleSave}
                shouldModalOpen={false}
                onCancel={() => {
                  setIsEditModalOpen(false)
                }}
                // TODO: Might require later
                // showFooter={true}
                layoutId={editLayout?.id}
              />
            ) : null}
          </div>
        </Modal>
      )}
      <FormProvider {...methods}>
        {layout?.entity_name ? (
          <FormAncestryScope
            entityName={layout.entity_name}
            layoutUid={layout.uid}
            getValues={getValues}
            isRootInstance={isRootEntityTab}
          >
            {accordionContent}
          </FormAncestryScope>
        ) : (
          accordionContent
        )}
      </FormProvider>
    </div>
  )
}

export default RowRenderTabs
